import { ParsedDeliverable } from '@/utils/deliverableParser'
import { exportDocument, exportDocumentToGdocs } from '@/features/tools/api'

/**
 * Compares two artifacts by their metadata to determine if they are the same
 * @param a - First artifact to compare
 * @param b - Second artifact to compare
 * @returns true if both artifacts have the same type and title (and content if title is undefined)
 */
export const isSameArtifact = (
  a: ParsedDeliverable | null,
  b: ParsedDeliverable | null,
): boolean => {
  if (!a || !b) return false

  // Primero comparar type
  if (a.metadata.type !== b.metadata.type) return false

  // Si ambos tienen título, comparar por título
  if (a.metadata.title && b.metadata.title) {
    return a.metadata.title === b.metadata.title
  }

  // Si uno tiene título y el otro no, son diferentes
  if (a.metadata.title !== b.metadata.title) return false

  // Si ambos no tienen título, comparar por contenido para desambiguar
  return a.content === b.content
}

/**
 * Prepares deliverable metadata for export
 */
const prepareDeliverableMetadata = (deliverable: ParsedDeliverable) => {
  return {
    title: deliverable.metadata.title,
    name:
      deliverable.metadata.title || `entregable-${deliverable.metadata.type}`,
    type: deliverable.metadata.type,
    description: `Entregable tipo: ${deliverable.metadata.type}`,
  }
}

/**
 * Exports a deliverable to PDF format
 * @param deliverable - The deliverable to export
 * @returns Promise that resolves when the download starts
 * @throws Error if the export fails
 */
export const exportDeliverableToPdf = async (
  deliverable: ParsedDeliverable,
): Promise<void> => {
  const metadata = prepareDeliverableMetadata(deliverable)
  await exportDocument(deliverable.content, metadata, 'pdf')
}

/**
 * Exports a deliverable to Word (.docx) format
 * @param deliverable - The deliverable to export
 * @returns Promise that resolves when the download starts
 * @throws Error if the export fails
 */
export const exportDeliverableToWord = async (
  deliverable: ParsedDeliverable,
): Promise<void> => {
  const metadata = prepareDeliverableMetadata(deliverable)
  await exportDocument(deliverable.content, metadata, 'docx')
}

/**
 * Exports a deliverable to Google Docs
 * @param deliverable - The deliverable to export
 * @returns Promise that resolves with the Google Docs document URL
 * @throws Error if the export fails or if the user is not authorized
 */
export const exportDeliverableToGoogleDocs = async (
  deliverable: ParsedDeliverable,
): Promise<string> => {
  const metadata = prepareDeliverableMetadata(deliverable)
  const documentUrl = await exportDocumentToGdocs(deliverable.content, metadata)
  return documentUrl
}
